/**
 * CRITICAL: AUTH CIRCUIT BREAKER - DO NOT MODIFY
 * 
 * This implements circuit breaker pattern to protect auth system.
 * It prevents cascading failures and isolates auth from other features.
 * 
 * RULES:
 * 1. NO feature can disable or bypass this protection
 * 2. NO modifications without security review
 * 3. ALWAYS fails safe (denies access when in doubt)
 * 4. NO external dependencies except timers
 */

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

interface CircuitBreakerConfig {
  failureThreshold: number;
  recoveryTimeout: number;
  monitoringWindow: number;
}

interface AuthOperation<T> {
  (): Promise<T>;
}

/**
 * IMMUTABLE AUTH CIRCUIT BREAKER
 * 
 * Protects auth operations from cascading failures.
 * Cannot be bypassed or disabled.
 */
class AuthCircuitBreaker {
  private static instance: AuthCircuitBreaker | null = null;
  private state: CircuitState = 'CLOSED';
  private failureCount = 0;
  private lastFailureTime = 0;
  private successCount = 0;
  
  private readonly config: CircuitBreakerConfig = {
    failureThreshold: 5,      // Open circuit after 5 failures
    recoveryTimeout: 30000,   // Wait 30 seconds before trying again
    monitoringWindow: 60000   // Reset counters every minute
  };

  private constructor() {
    // Start monitoring window reset timer
    setInterval(() => {
      this.resetCounters();
    }, this.config.monitoringWindow);

    // Seal the instance
    Object.seal(this);
  }

  /**
   * Singleton pattern
   */
  public static getInstance(): AuthCircuitBreaker {
    if (!AuthCircuitBreaker.instance) {
      AuthCircuitBreaker.instance = new AuthCircuitBreaker();
    }
    return AuthCircuitBreaker.instance;
  }

  /**
   * Execute auth operation with circuit breaker protection
   */
  public async execute<T>(operation: AuthOperation<T>, operationName: string): Promise<T> {
    // Check if circuit is open
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime < this.config.recoveryTimeout) {
        throw new Error(`AUTH_CIRCUIT_OPEN: ${operationName} blocked by circuit breaker`);
      } else {
        // Try to recover
        this.state = 'HALF_OPEN';
      }
    }

    try {
      // Execute the operation
      const result = await operation();
      
      // Operation succeeded
      this.onSuccess(operationName);
      return result;
      
    } catch (error) {
      // Operation failed
      this.onFailure(operationName, error);
      throw error;
    }
  }

  /**
   * Get current circuit state
   */
  public getState(): CircuitState {
    return this.state;
  }

  /**
   * Get circuit statistics
   */
  public getStats() {
    return Object.freeze({
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      lastFailureTime: this.lastFailureTime
    });
  }

  /**
   * Force circuit to open (emergency use only)
   */
  public emergencyOpen(reason: string): void {
    console.error(`AUTH CIRCUIT BREAKER: Emergency open - ${reason}`);
    this.state = 'OPEN';
    this.lastFailureTime = Date.now();
  }

  /**
   * Handle successful operation
   */
  private onSuccess(operationName: string): void {
    this.successCount++;
    
    if (this.state === 'HALF_OPEN') {
      // Recovery successful
      this.state = 'CLOSED';
      this.failureCount = 0;
    }
  }

  /**
   * Handle failed operation
   */
  private onFailure(operationName: string, error: any): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    console.error(`Auth circuit breaker: Failure in ${operationName}:`, error);
    
    // Open circuit if failure threshold reached
    if (this.failureCount >= this.config.failureThreshold) {
      this.state = 'OPEN';
      console.error(`AUTH CIRCUIT BREAKER: Circuit opened due to ${this.failureCount} failures`);
    }
  }

  /**
   * Reset failure counters (called periodically)
   */
  private resetCounters(): void {
    if (this.state === 'CLOSED') {
      this.failureCount = 0;
      this.successCount = 0;
    }
  }
}

/**
 * AUTH PROTECTION WRAPPER
 * 
 * Wraps auth operations with circuit breaker protection.
 * Cannot be bypassed or disabled.
 */
export class AuthProtection {
  private static circuitBreaker = AuthCircuitBreaker.getInstance();

  /**
   * Protect auth operation with circuit breaker
   */
  public static async protect<T>(
    operation: AuthOperation<T>,
    operationName: string
  ): Promise<T> {
    return this.circuitBreaker.execute(operation, operationName);
  }

  /**
   * Get protection status
   */
  public static getStatus() {
    return this.circuitBreaker.getStats();
  }

  /**
   * Emergency protection activation
   */
  public static emergencyProtect(reason: string): void {
    this.circuitBreaker.emergencyOpen(reason);
  }
}

// Export singleton instance
export const authCircuitBreaker = AuthCircuitBreaker.getInstance();

// Prevent modifications
Object.freeze(AuthProtection);
// Object.freeze(authCircuitBreaker); // Commented out to allow resetCounters() to modify failureCount
